import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X, Loader2, AlertCircle, Search, Check } from 'lucide-react';
import axiosInstance from '../api/axios';

/**
 * Multi-select dropdown for canonical systems, emitting an array of system
 * UUIDs (NOT names). Used on report create/edit pages to pick which systems
 * a report maps to.
 *
 *   value     = ["uuid-a", "uuid-b"]
 *   onChange  = (next: string[]) => void
 *
 * Reuses the visual pattern of SystemMultiSelect but skips the add-new flow
 * (creating a system from inside a report edit is out of scope; users go to
 * the Systems page for that).
 */
function SystemPickerByUuid({ value, onChange, error }) {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const selectedSet = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await axiosInstance.get('/api/systems', { params: { page_size: 200 } });
        if (cancelled) return;
        setSystems(Array.isArray(res.data?.systems) ? res.data.systems : []);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.error || 'Failed to load systems');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filteredSystems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return systems;
    return systems.filter((s) =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.systemId || '').toLowerCase().includes(q) ||
      (s.systemOwner || '').toLowerCase().includes(q)
    );
  }, [systems, query]);

  const systemById = useMemo(() => {
    const map = new Map();
    for (const s of systems) {
      if (s.id) map.set(s.id, s);
    }
    return map;
  }, [systems]);

  const selectedItems = useMemo(() =>
    (Array.isArray(value) ? value : [])
      .map((id) => systemById.get(id))
      .filter(Boolean),
    [value, systemById]
  );

  const toggle = (system) => {
    if (!system?.id) return;
    const next = new Set(selectedSet);
    if (next.has(system.id)) next.delete(system.id);
    else next.add(system.id);
    onChange(Array.from(next));
  };

  const remove = (id) => {
    onChange((Array.isArray(value) ? value : []).filter((v) => v !== id));
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex flex-wrap items-center gap-1.5 min-h-[42px] px-3 py-1.5 border rounded-lg bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        {selectedItems.length === 0 ? (
          <span className="text-gray-400 text-sm">Select systems...</span>
        ) : (
          selectedItems.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded"
            >
              {s.name}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); remove(s.id); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); remove(s.id); } }}
                className="hover:bg-blue-100 rounded p-0.5 inline-flex cursor-pointer"
                aria-label={`Remove ${s.name}`}
              >
                <X className="h-3 w-3" />
              </span>
            </span>
          ))
        )}
        <ChevronDown className="h-4 w-4 text-gray-400 ml-auto shrink-0" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search systems..."
                className="w-full pl-8 pr-2 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            {loading ? (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                Loading systems…
              </div>
            ) : loadError ? (
              <div className="px-3 py-4 text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {loadError}
              </div>
            ) : filteredSystems.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500">
                {query ? `No systems match "${query}".` : 'No systems in the catalog yet.'}
              </div>
            ) : (
              filteredSystems.map((s) => {
                const checked = selectedSet.has(s.id);
                return (
                  <button
                    key={s.id || s.systemId || s.name}
                    type="button"
                    onClick={() => toggle(s)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                  >
                    <span
                      className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                        checked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300'
                      }`}
                      aria-hidden="true"
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 min-w-0 truncate">{s.name}</span>
                    {s.systemId && (
                      <span className="text-xs text-gray-400 font-mono shrink-0">{s.systemId}</span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemPickerByUuid;
